(() => {
  'use strict';
  if (customElements.get('solago-bm-finder')) return;

  class Finder extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      const signal = this.controller.signal;
      this.form = this.querySelector('form');
      if (!this.form) return;
      this.names = ['location', 'people', 'sun'];
      this.results = this.querySelector('[data-results]');
      this.grid = this.querySelector('[data-product-grid]');
      this.submit = this.querySelector('[type="submit"]');
      this.form.addEventListener('change', event => {
        this.invalidate();
        if (matchMedia('(max-width:899px)').matches) {
          const next = this.names[this.names.indexOf(event.target.name) + 1];
          if (next) this.setStep(next);
        }
      }, {signal});
      this.form.addEventListener('submit', event => {
        event.preventDefault();
        if (!this.form.reportValidity() || !this.names.every(name => this.values()[name])) return;
        this.showResults();
      }, {signal});
      for (const button of this.querySelectorAll('[data-question-step]')) button.addEventListener('click', () => this.setStep(button.dataset.questionStep), {signal});
      this.querySelector('[data-edit]')?.addEventListener('click', () => {
        this.setStep('location');
        this.form.scrollIntoView({behavior: 'smooth', block: 'center'});
        this.form.querySelector('input:checked')?.focus({preventScroll: true});
      }, {signal});
      if (new URL(location.href).searchParams.get('open') === '1') this.querySelector('details').open = true;
      this.invalidate();
    }
    values() { return Object.fromEntries(this.names.map(name => [name, new FormData(this.form).get(name)])); }
    setStep(name) {
      this.dataset.step = name;
      for (const button of this.querySelectorAll('[data-question-step]')) button.setAttribute('aria-pressed', String(button.dataset.questionStep === name));
    }
    invalidate() {
      const values = this.values();
      const count = this.names.filter(name => values[name]).length;
      this.querySelector('[data-progress]').textContent = `${count} von 3 beantwortet`;
      this.submit.hidden = count !== 3;
      this.results.hidden = true;
      this.grid.replaceChildren();
    }
    matchProducts(values) {
      if (!values.location || !values.people || !values.sun || values.sun === 'shade') return [];
      const mappings = [...this.querySelectorAll('template[data-finder-products]')];
      const exact = mappings.find(t => t.dataset.location === values.location && t.dataset.people === values.people);
      const selected = exact || mappings.find(t => t.dataset.location === values.location && t.dataset.people === 'all');
      if (!selected) return [];
      const products = [...selected.content.children].filter(p => p.classList.contains('sbm-native-card') && p.dataset.available !== 'false');
      if (!exact && products.every(p => Number(p.dataset.moduleCount) > 0)) {
        const priority = values.people === 'one' ? [2,3,4] : values.people === 'two' ? [3,4,2] : [4,3,2];
        const rank = p => { const n = Number(p.dataset.moduleCount); const i = priority.indexOf(n); return i === -1 ? priority.length + n : i; };
        products.sort((a,b) => rank(a) - rank(b));
      }
      return products;
    }
    showResults() {
      const values = this.values();
      const products = this.matchProducts(values);
      this.grid.replaceChildren(...products.map(p => p.cloneNode(true)));
      this.grid.hidden = products.length === 0;
      const locationNames = {balcony: 'deinen Balkon', flat: 'Garage & Flachdach', roof: 'dein Hausdach', garden: 'Garten & Terrasse'};
      const tags = this.names.map(name => this.form.querySelector(`input[name="${name}"]:checked`)?.closest('label')?.querySelector('strong')?.textContent || '');
      this.querySelector('[data-answer-tags]').replaceChildren(...tags.map(text => { const li = document.createElement('li'); li.textContent = text; return li; }));
      this.querySelector('[data-result-title]').textContent = products.length ? `Deine Set-Auswahl für ${locationNames[values.location]}` : values.sun === 'shade' ? 'Prüfen wir zuerst deinen Standort.' : 'Gemeinsam finden wir das passende Set.';
      this.querySelector('[data-result-message]').textContent = !products.length ? '' : values.sun === 'partial' ? 'Diese Sets passen zum gewählten Montageort. Prüfe bei zeitweisem Schatten vor dem Kauf, wann deine Fläche direktes Sonnenlicht bekommt.' : 'Diese Sets passen zum gewählten Montageort. Vergleiche vor dem Kauf noch Platzbedarf und deinen tatsächlichen Stromverbrauch.';
      const empty = this.querySelector('[data-no-results]');
      empty.hidden = products.length !== 0;
      empty.querySelector('h4').textContent = values.sun === 'shade' ? 'Bei viel Schatten kommt es auf den Standort an.' : 'Wir helfen dir bei deiner Auswahl.';
      empty.querySelector('p').textContent = values.sun === 'shade' ? 'Damit deine Anlage sinnvoll Strom erzeugen kann, schauen wir uns am besten zuerst deine Fläche und die Sonneneinstrahlung an.' : 'Für diese Auswahl sind aktuell keine passenden Sets verfügbar. Unser Team unterstützt dich bei der Auswahl.';
      this.results.hidden = false;
      this.results.scrollIntoView({behavior: matchMedia('(prefers-reduced-motion:reduce)').matches ? 'instant' : 'smooth', block: 'start'});
      this.results.querySelector('[data-result-title]').focus({preventScroll: true});
    }
    disconnectedCallback() { this.controller?.abort(); }
  }

  class Storage extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      this.addEventListener('click', (event) => {
        const button = event.target.closest('[data-energy]');
        if (!button || !this.contains(button)) return;
        const night = button.dataset.energy === 'night';
        for (const option of this.querySelectorAll('[data-energy]')) option.setAttribute('aria-pressed', String(option === button));
        this.querySelector('[data-scene]').dataset.scene = night ? 'night' : 'day';
        const sky = this.querySelector('[data-sky]');
        const sourceIcon = button.querySelector('svg');
        if (sourceIcon) sky.replaceChildren(sourceIcon.cloneNode(true));
        this.querySelector('[data-flow-label]').textContent = night ? 'Speicher → dein Zuhause' : 'Überschüsse speichern';
        this.querySelector('[data-energy-title]').textContent = night ? 'Abends deinen gespeicherten Strom nutzen.' : 'Tagsüber erzeugen. Überschüsse speichern.';
        this.querySelector('[data-energy-text]').textContent = night ? 'Auch wenn die Sonne nicht mehr scheint, kannst du zuvor gespeicherten Solarstrom nutzen. Wie lange, hängt vom Füllstand und deinem Verbrauch ab.' : 'Deine Module erzeugen Solarstrom für dein Zuhause. Strom, den du gerade nicht benötigst, kann der Speicher für später aufbewahren.';
      }, {signal:this.controller.signal});
    }
    disconnectedCallback() { this.controller?.abort(); }
  }

  class ProductStatus extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      this.variants = JSON.parse(this.querySelector('[data-variants]')?.textContent || '{}');
      this.current = this.dataset.initialVariant;
      const signal = this.controller.signal;
      document.addEventListener('variant:changed', (event) => {
        const id = event.detail?.variant?.id;
        const target = event.target;
        if (this.form && target?.closest?.('form') !== this.form) return;
        if (this.dataset.formId && target?.id !== this.dataset.formId && !target?.closest?.(`#${CSS.escape(this.dataset.formId)}`)) return;
        // Ignore quick-buy dialogs and recommendations belonging to another product.
        if (id != null && !Object.hasOwn(this.variants, String(id))) return;
        if (id == null && !this.ownsForm(target)) return;
        this.update(id);
      }, {signal});
      document.addEventListener('change', (event) => {
        const target = event.target;
        if (target?.name === 'id' && this.ownsForm(target)) this.update(target.value);
      }, {signal});
      window.addEventListener('popstate', () => {
        const id = new URL(location.href).searchParams.get('variant');
        if (id && Object.hasOwn(this.variants, id)) this.update(id);
      }, {signal});
      // Read the product form in case a configurator changed selection before this element loaded.
      const forms = [...document.querySelectorAll('form[action*="/cart/add"]')];
      const form = this.dataset.formId ? document.getElementById(this.dataset.formId) : forms.find(f => {
        const id = f.querySelector('[name="id"]')?.value;
        return Object.hasOwn(this.variants,id) && !f.closest('quick-buy-drawer,quick-buy-popover,.quick-buy');
      });
      if (form) this.form = form;
      this.update(form?.querySelector('[name="id"]')?.value || this.current);
    }
    ownsForm(target) {
      const form = target?.closest?.('form');
      if (!form) return false;
      if (this.dataset.formId) return form.id === this.dataset.formId;
      if (this.form) return form === this.form;
      const id = form.querySelector('[name="id"]')?.value;
      return Object.hasOwn(this.variants,id);
    }
    update(id) {
      this.current = id;
      const variant = this.variants[String(id)];
      const eligible = variant?.eligible === true && variant.available === true;
      this.dataset.eligible = String(eligible);
      this.querySelector('[data-status-title]').textContent = eligible ? 'Dein Set erhält eine Anker Powerbank gratis dazu.' : 'Diese Auswahl nimmt aktuell nicht an der Geschenkaktion teil.';
      this.querySelector('[data-status-text]').textContent = eligible ? 'Diese verfügbare Variante nimmt an der Black-Month-Aktion teil.' : 'Die Aktion gilt für verfügbare, teilnehmende Sets ab 4 Solarmodulen mit Anker SOLIX 4 Pro Speicher.';
    }
    disconnectedCallback() { this.controller?.abort(); }
  }

  class CartStatus extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      const queue = () => { clearTimeout(this.timer); this.timer = setTimeout(() => this.refresh(), 120); };
      for (const name of ['cart:updated','cart:refresh','cart:item:added']) document.addEventListener(name, queue, {signal:this.controller.signal});
      this.addEventListener('solago-bm:refresh', queue, {signal:this.controller.signal});
    }
    async refresh() {
      this.fetchController?.abort();
      this.fetchController = new AbortController();
      const root = window.Shopify?.routes?.root || '/';
      const url = new URL(root, location.origin);
      url.searchParams.set('section_id', this.dataset.refreshSection);
      try {
        const response = await fetch(url, {signal:this.fetchController.signal,credentials:'same-origin',cache:'no-store'});
        if (!response.ok) throw new Error('Cart section unavailable');
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const content = html.querySelector('[data-cart-status-content]');
        if (!this.isConnected) return;
        if (!content) throw new Error('Cart status content unavailable');
        this.querySelector('[data-cart-status-content]').replaceChildren(...content.childNodes);
      } catch (error) {
        if (error.name !== 'AbortError' && this.isConnected) {
          // Remove stale positive claims if the current cart cannot be verified.
          this.querySelector('[data-cart-status-content]')?.replaceChildren();
        }
      }
    }
    disconnectedCallback() { this.controller?.abort(); this.fetchController?.abort(); clearTimeout(this.timer); }
  }
  customElements.define('solago-bm-finder', Finder);
  customElements.define('solago-bm-storage', Storage);
  customElements.define('solago-bm-product-status', ProductStatus);
  customElements.define('solago-bm-cart-status', CartStatus);
})();
